import PropTypes from 'prop-types';
import { useState, useRef, useEffect, useMemo } from 'react';
import { Card, Col, Row, Nav, InputGroup, Button } from 'react-bootstrap';
import useScrollSpy from 'react-use-scrollspy';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { FormProvider, RHFControl, RHFSelect } from 'components/common/form';
import { packageScheme } from './packageScheme';
import { PackagesDetails } from './PackagesDetails';
import { ChecksOptionsCard } from '../../../common/ChecksOptionsCard';
import { locationTypesMap } from 'components/donations/donation-settings/Locations';
import paths from 'routes/paths';
import { donationSettingsSectionsRefMap } from 'components/donations/donation-settings';
import LoadingButton from 'components/common/LoadingButton';
import { formatMoney } from 'helpers/utils';
import {
  useLocations,
  useCampaigns,
  useDonationTypes,
  useDonationAmounts,
  useTransactionFees,
  useDonationCurrencies,
  useCurrencies
} from 'hooks/useDonationSettings';

export const PAYMENT_OPTION_ONE_OFF = 'one-off';
export const PAYMENT_OPTION_MONTHLY = 'monthly';
export const PAYMENT_OPTION_YEARLY = 'yearly';

const paymentOptions = [PAYMENT_OPTION_ONE_OFF, PAYMENT_OPTION_MONTHLY];

export const getTrueKeys = obj => {
  const trueKeys = [];
  Object.keys(obj).forEach(key => {
    if (obj[key]) {
      trueKeys.push(key);
    }
  });
  return trueKeys;
};

// const convertArrToObj = arr => {
//   const obj = {};
//   arr.forEach(item => {
//     obj[item] = true;
//   });
//   return obj;
// };

// const getObjInitialValues = (data,checkboxes) => {
//   const obj = {};
//   data.forEach(item => {
//     const itemExists = checkboxes.find(checkbox => checkbox.value === item);

const PackageForm = ({ onSubmit, loading, isEdit, data }) => {
  const navigate = useNavigate();
  const { locations: donationLocationsData } = useLocations(
    locationTypesMap.project
  );
  const { campaigns: donationCampaignsData } = useCampaigns();
  const { donationTypes: donationTypesData } = useDonationTypes();
  const { donationAmounts: donationAmountsData } = useDonationAmounts();
  const { transactionFees: transactionFeesData } = useTransactionFees();
  const { donationCurrencies: donationCurrenciesData } = useDonationCurrencies();
  const { currencies: currenciesData } = useCurrencies();

  const methods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      title: '',
      /*
      currency: '',
      targetAmount: '',
       */
      description: '',
      // selectAll: false,
      paymentOptions: {
        [PAYMENT_OPTION_ONE_OFF]: false,
        [PAYMENT_OPTION_MONTHLY]: false,
        [PAYMENT_OPTION_YEARLY]: false
      },
      locations: {
        all: false
      },
      /* categories: {}, Hidden for now */
      campaigns: {},
      donationTypes: {},
      donationAmounts: {},
      transactionFeeId: '',
      currencyId: '',
      has_custom_donation_amount: false,
      newAmount: ''
    },
    resolver: yupResolver(packageScheme)
  });
  const { handleSubmit: RHFHandleSubmit, setValue, getValues } = methods;

  // const openTransactionFee = watch('openTransactionFee');
  // const openRedirectUrl = watch('openRedirectUrl');

  // const handleOpenTransactionFee = e => {
  //   setValue('openTransactionFee', e.target.checked);
  // };
  // const handleOpenRedirectUrl = e => {
  //   setValue('openRedirectUrl', e.target.checked);
  // };
  // useEffect(() => {
  //   if (data) {
  const sectionRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null)
  ];

  const activeSection = useScrollSpy({
    sectionElementRefs: sectionRefs,
    offsetPx: -150
  });

  const [stickyStatus, setStickyStatus] = useState(false);
  const [customAmounts, setCustomAmounts] = useState([]);

  useEffect(() => {
    window.addEventListener('scroll', () => {
      if (window.scrollY < 100) {
        setStickyStatus(false);
      } else {
        setStickyStatus(true);
      }
    });
  }, []);

  const sideNavLinks = [
    { id: 1, href: '#details', title: 'Packages Details' },
    { id: 2, href: '#payments', title: 'Payments Options' },
    { id: 3, href: '#location', title: 'Locations' },
    { id: 4, href: '#campaigns', title: 'Campaigns' },
    { id: 5, href: '#donationType', title: 'Donation Types' },
    { id: 6, href: '#donationAmounts', title: 'Donation Amounts' },
    { id: 7, href: '#transactionFees', title: 'Transaction Fees' },
    { id: 8, href: '#donationCurrencies', title: 'Currencies' }
  ];
  const locationCheckboxes = useMemo(() => {
    const checkboxes = donationLocationsData?.map(location => {
      if (isEdit) {
        const itemExists = data.location?.find(
          location_id => location_id === location.id
        );
        setValue(`locations.${location.id}`, itemExists ? true : false);
      } else {
        setValue(`locations.${location.id}`, false);
      }
      return {
        name: `locations.${location.id}`,
        label: location.location_name
      };
    });

    return [{ name: 'locations.all', label: 'All' }, ...(checkboxes || [])];
  }, [donationLocationsData, data?.location, isEdit, setValue]);
  /*
  const categoryCheckboxes = useMemo(
    () =>
      donationCategoriesData?.package_category?.map(category => {
        if (isEdit) {
          const itemExists = data.package_category?.find(
            category_id => category_id === category.id
          );
          setValue(`categories.${category.id}`, itemExists ? true : false);
        }
        return {
          name: `categories.${category.id}`,
          label: category.category_name
        };
      }),
    [donationCategoriesData, data?.package_category, isEdit, setValue]
  );
   */
  const campaignCheckboxes = useMemo(
    () =>
      donationCampaignsData?.map(campaign => {
        if (isEdit) {
          const itemExists = data.campaign?.find(
            campaign_id => campaign_id === campaign.id
          );
          setValue(`campaigns.${campaign.id}`, itemExists ? true : false);
        } else {
          setValue(`campaigns.${campaign.id}`, false);
        }

        return {
          name: `campaigns.${campaign.id}`,
          label: campaign.name
        };
      }),
    [donationCampaignsData, data?.campaigns, isEdit, setValue]
  );
  const donationTypeCheckboxes = useMemo(
    () =>
      donationTypesData?.map(donationType => {
        if (isEdit) {
          const itemExists = data.donation_type?.find(
            donation_type_id => donation_type_id === donationType.id
          );
          setValue(
            `donationTypes.${donationType.id}`,
            itemExists ? true : false
          );
        } else {
          setValue(`donationTypes.${donationType.id}`, false);
        }

        return {
          name: `donationTypes.${donationType.id}`,
          label: donationType.donation_type_name
        };
      }),
    [donationTypesData]
  );

  useEffect(() => {
    if (isEdit && donationAmountsData) {
      const allAmounts = donationAmountsData?.map(
        donationAmount => donationAmount.amount
      );
      const selectedRealAmounts = data?.donation_amount?.filter(
        amount => amount !== 'custom'
      );
      const hasCustomAmount = data?.donation_amount?.find(
        amount => amount === 'custom'
      );
      const newCustomAmounts = selectedRealAmounts?.filter(
        amount => !allAmounts.includes(amount)
      );
      setCustomAmounts(newCustomAmounts);
      // set form values for checkboxes custom amounts
      newCustomAmounts.forEach(amount => {
        const itemExists = selectedRealAmounts?.find(
          donation_amount => donation_amount === amount
        );
        setValue(`donationAmounts.${amount}`, !!itemExists);
      });
      // set form values for checkboxes donation amounts
      console.log('allAmounts', allAmounts);
      console.log('donation_amount', data.donation_amount);

      allAmounts?.forEach(amount => {
        const itemExists = selectedRealAmounts?.find(
          donation_amount => donation_amount === amount
        );
        console.log({ amount, itemExists });
        setValue(`donationAmounts.${amount}`, !!itemExists);
      });

      setValue('donationAmounts.custom', !!hasCustomAmount);
    }
  }, [data, isEdit, donationAmountsData, setValue]);

  const donationAmountCheckboxes = useMemo(() => {
    const checkboxes = donationAmountsData
      ?.filter(donationAmount => donationAmount.amount !== 'custom')
      ?.map(donationAmount => {
        return {
          name: `donationAmounts.${donationAmount.amount}`,
          label: formatMoney(donationAmount.amount)
        };
      });

    const customAmountCheckboxes = customAmounts.map(amount => ({
      name: `donationAmounts.${amount}`,
      label: formatMoney(amount)
    }));

    return [
      {
        name: 'donationAmounts.custom',
        label: 'Other Amount'
      },
      ...(checkboxes || []),
      ...customAmountCheckboxes
    ];
  }, [donationAmountsData, isEdit, data, setValue, customAmounts]);

  const handleAddAmount = () => {
    const newAmount = getValues('newAmount');
    if (newAmount) {
      setValue(`donationAmounts.${newAmount}`, true);
      setCustomAmounts([...customAmounts, parseFloat(newAmount)]);
      setValue('newAmount', '');
    }
  };

  // Transaction fee dropdown options
  const transactionFeeOptions = useMemo(
    () =>
      transactionFeesData?.map(transactionFee => ({
        value: transactionFee.id,
        label: `${transactionFee.amount} ${
          transactionFee.fee_type === 'percentage' ? '%' : 'Fixed'
        }`
      })) || [],
    [transactionFeesData]
  );

  // Currency dropdown options
  const currencyOptions = useMemo(
    () =>
      donationCurrenciesData?.map(donationCurrency => {
        const currencyDetails = currenciesData?.find(
          c => c.id === donationCurrency.currency_id
        );
        return {
          value: donationCurrency.currency_id,
          label: currencyDetails
            ? `${currencyDetails.abbreviation} (${currencyDetails.symbol})`
            : donationCurrency.currency_id
        };
      }) || [],
    [donationCurrenciesData, currenciesData]
  );
  const CARDS = useMemo(
    () => [
      // {
      //   title: 'Branches',
      //   id: 'branches',
      //   check: true,
      //   value: '',
      //   isEdit: true,
      //   switch: false,
      //   checkboxes: campaignCheckboxes
      // },
      {
        title: 'Current Payments Options',
        id: 'current_payments_options',
        value: '',
        check: true,
        isEdit: false,
        switch: false,
        checkboxes: [
          {
            name: `paymentOptions.${PAYMENT_OPTION_ONE_OFF}`,
            label: 'One-off'
          },
          {
            name: `paymentOptions.${PAYMENT_OPTION_MONTHLY}`,
            label: 'Monthly'
          },
          {
            name: `paymentOptions.${PAYMENT_OPTION_YEARLY}`,
            label: 'Yearly'
          }
        ]
      },
      {
        title: 'Current Locations',
        id: 'locations',
        value: '',
        isEdit: true,
        check: true,
        switch: false,
        editHref: donationSettingsSectionsRefMap.location,
        checkboxes: locationCheckboxes
      },
      /*
      {
        title: 'Current Categories List',
        id: 'current_categories_list',
        value: '',
        check: true,
        isEdit: true,
        switch: false,
        editHref: donationSettingsSectionsRefMap.category,
        checkboxes: categoryCheckboxes
      },
      */
      {
        title: 'Current Campaigns List',
        id: 'current_campaigns_list',
        value: '',
        check: true,
        isEdit: true,
        switch: false,
        editHref: donationSettingsSectionsRefMap.campaigns,
        checkboxes: campaignCheckboxes
      },
      {
        title: 'Current Donation Types',
        id: 'current_donation_types',
        value: '',
        check: true,
        isEdit: true,
        switch: false,
        editHref: donationSettingsSectionsRefMap.donationType,
        checkboxes: donationTypeCheckboxes
      },
      {
        title: 'Current Donation Amounts',
        id: 'current_donation_amounts',
        value: '',
        isEdit: true,
        check: true,
        switch: false,
        editHref: donationSettingsSectionsRefMap.donationAmount,
        checkboxes: donationAmountCheckboxes
      }
    ],
    [
      locationCheckboxes,
      campaignCheckboxes,
      donationTypeCheckboxes,
      donationAmountCheckboxes
    ]
  );
  const onEditClick = href => {
    navigate(paths.donationSettings + href);
  };
  useEffect(() => {
    if (isEdit) {
      const {
        currency_id,
        transaction_fee_id,
        package_title,
        package_description,
        payment_options
      } = data;
      setValue('title', package_title);
      setValue('description', package_description);
      setValue('currencyId', currency_id || '');
      setValue('transactionFeeId', transaction_fee_id || '');
      payment_options?.forEach(id => {
        const itemExists = paymentOptions.find(
          payment_option_id => payment_option_id === id
        );
        setValue(`paymentOptions.${id}`, itemExists ? true : false);
      });
    }
  }, [data, isEdit, setValue]);

  // Set default transaction fee to 2.5% for new packages
  useEffect(() => {
    if (!isEdit && transactionFeesData && transactionFeesData.length > 0) {
      const defaultFee = transactionFeesData.find(
        fee => fee.amount === 2.5 && fee.fee_type === 'percentage'
      );
      if (defaultFee) {
        setValue('transactionFeeId', defaultFee.id);
      }
    }
  }, [isEdit, transactionFeesData, setValue]);

  const handleSubmit = data => {
    const {
      paymentOptions,
      locations,
      campaigns,
      donationTypes,
      donationAmounts,
      transactionFeeId,
      currencyId
    } = data;
    const paymentOptionsData = getTrueKeys(paymentOptions);
    let locationsData = [];
    if (locations.all && donationLocationsData) {
      locationsData = donationLocationsData.map(location => location.id);
    } else {
      locationsData = getTrueKeys(locations);
    }
    const campaignsData = getTrueKeys(campaigns);
    const donationTypesIds = getTrueKeys(donationTypes);
    const donationTypesWithLabels = [];
    donationTypesIds.forEach(donationId => {
      const donationType = donationTypesData?.find(
        type => type.id === donationId
      );
      if (donationType) {
        donationTypesWithLabels.push({
          id: donationId,
          type: donationType.donation_type_name
        });
      }
    });
    const donationAmountsWithLabels = getTrueKeys(donationAmounts).map(
      amount => ({
        amount: amount === 'custom' ? amount : parseFloat(amount)
      })
    );

    const dataToSubmit = {
      donation_amount: donationAmountsWithLabels.map(item => item.amount),
      donation_amounts: JSON.stringify(donationAmountsWithLabels),
      donation_type: donationTypesIds,
      donation_types: JSON.stringify(donationTypesWithLabels),
      location: locationsData,
      package_description: data.description,
      package_title: data.title,
      payment_options: paymentOptionsData,
      campaign: campaignsData,
      transaction_fee_id: transactionFeeId || null,
      currency_id: currencyId || null
    };
    onSubmit(dataToSubmit);
  };

  return (
    <FormProvider methods={methods} onSubmit={RHFHandleSubmit(handleSubmit)}>
      <Row className="pt-3 gx-3">
        <Col md={9}>
          <PackagesDetails
            sectionRef={sectionRefs[1]}
            sideNavLinks={sideNavLinks[1]?.href?.substring(1)}
          />

          {CARDS.map((card, index) => {
            return (
              <ChecksOptionsCard
                key={index}
                sectionRef={sectionRefs[index + 2]}
                sideNavLinks={sideNavLinks[index + 2]?.href?.substring(1)}
                card={card}
                onEdit={() => onEditClick(card.editHref)}
                // handleChange={handleChange}
                // handleCheck={handleCheck}
                // editData={editData}
                // deleteData={deleteData}
                // addData={addData}
              >
                {card.id === 'current_donation_amounts' && (
                  <InputGroup className="mb-3">
                    <div className="flex-grow-1">
                      <RHFControl
                        name="newAmount"
                        label="Add new Amount"
                        type="number"
                        placeholder="15"
                        noOptional
                      />
                    </div>
                    {/* <Can I={userActions.update} a={subject}> */}
                    <Button
                      // loading={isSaveRedirectionPageLoading}
                      variant="light"
                      type="button"
                      onClick={handleAddAmount}
                      className="border bg-transparent shadow-none align-self-end"
                      style={{
                        fontWeight: 600,
                        maxWidth: '100px'
                      }}
                    >
                      + Add
                    </Button>
                    {/* </Can> */}
                  </InputGroup>
                )}
              </ChecksOptionsCard>
            );
          })}

          {/* Transaction Fee & Currency Dropdowns */}
          <Card className="border shadow-none mb-3">
            <Card.Header className="pb-0">
              <Card.Title as="h5">Transaction Fee & Currency</Card.Title>
            </Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col md={6}>
                  <RHFSelect
                    name="transactionFeeId"
                    label="Transaction Fee"
                    options={transactionFeeOptions}
                    placeholder="Select transaction fee..."
                  />
                </Col>
                <Col md={6}>
                  <RHFSelect
                    name="currencyId"
                    label="Currency"
                    options={currencyOptions}
                    placeholder="Select currency..."
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="border shadow-none">
            <Card.Body className="d-flex flex-row justify-content-end">
              <LoadingButton
                variant="primary"
                className="w-100 w-sm-25"
                type="submit"
                loading={loading}
              >
                {isEdit ? 'Update' : 'Add'}
              </LoadingButton>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} className="order-0 order-md-1 d-none d-md-block">
          <Card
            className={'mb-3 sticky-top border shadow-none'}
            style={{ top: stickyStatus ? '5rem' : '0' }}
          >
            <Card.Body>
              <div>
                <Nav className="flex-column" variant="pills">
                  {sideNavLinks?.map(item => (
                    <Nav.Link
                      key={item.id}
                      href={item.href}
                      className={`${
                        activeSection === item.id
                          ? 'current custom-nav-link'
                          : 'custom-nav-link'
                      }`}
                      active={activeSection >= item.id}
                    >
                      {item.title}
                    </Nav.Link>
                  ))}
                </Nav>
              </div>
            </Card.Body>
          </Card>
        </Col>
        {/* <DataModal
          show={modal}
          setShow={setModal}
          page="packages_details"
          data={{
            ...allData,
            packagesDetails: packagesDetails,
            successRedirection: successRedirection
          }}
        /> */}
      </Row>
    </FormProvider>
  );
};

PackageForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  data: PropTypes.object,
  isEdit: PropTypes.bool
};

export default PackageForm;
